import { useContext } from "react"
import PropTypes from "prop-types"
import { capitalize } from "lodash"
import { DESC } from "helpers/sorts"
import { useAssetViewDashboardMetrics } from "helpers/metrics/hooks"
import { INSTRUMENTS } from "connected-components/dashboard/helpers"
import { useTemplateGatherers } from "connected-components/dashboard/hooks/use-template-gatherers"
import ContributorsDataIntegrator from "gatherers/contributors-data-integrator"
import ContributorsTable from "renderers/contributors-datagrid"
import { CLASSIFICATIONS_SHAPE } from "helpers/shapes/asset-view"
import { useAnalytics } from "connected-components/analytics"
import { useAssetViewHeaderHandlers } from "../../hooks/use-asset-view-header-handlers"
import { EXPOSURE_CONTEXT } from "./context"

export default function AssetViewExposure({ modelMetadata, context, instrumentsWithMetadata, highlightedRowIds, disableActiveTrading, instrumentColumns, classifications }) {
  const { selectedCategory, selectedMetricDisplay } = useContext(EXPOSURE_CONTEXT)
  const {
    instrument: original,
    base: contextBase,
    groupBy,
    secondInstrument: rebalanced,
    comparisonInstrument: comparison,
    setTradedPosition,
    selectedTableLevels,
    setSelectedTableLevels,
    isExpanded,
    isEndVsStartDateComparison,
  } = useContext(context)

  const analytics = useAnalytics()
  const { handleSortChanged, handleTableNavigationChanged } = useAssetViewHeaderHandlers({ track: analytics.track })

  const gatherers = useTemplateGatherers(context, "exposure", {
    selectedCategory,
    selectedMetricDisplay,
    classifications,
  })

  const metrics = useAssetViewDashboardMetrics(
    [{ category: "exposure", selectedCategory }],
    original,
    rebalanced,
    comparison,
    { instrumentColumns, isActive: !!contextBase, modelMetadata, instrumentsWithMetadata, setTradedPosition, isExpanded, disableActiveTrading, selectedMetricDisplay, isEndVsStartDateComparison },
  )

  const defaultSortSuffix = INSTRUMENTS[instrumentColumns[0]].suffix

  return <ContributorsDataIntegrator
    gatherers={gatherers}
    metrics={metrics}
    Renderer={ContributorsTable}
    rendererProps={{
      highlightedRowIds,
      groupByName: groupBy?.label,
      defaultSort: {
        path: `marketValue${capitalize(defaultSortSuffix)}`,
        direction: DESC,
      },
      tableNavigationLevels: selectedTableLevels,
      onTableNavigationChange: setSelectedTableLevels,
      downloadKey: "assets.exposure",
      downloadName: "Assets - Exposure",
      // Analytics callbacks
      onSortChanged: handleSortChanged,
      onTableNavigationChanged: handleTableNavigationChanged,
      context,
    }}
  />
}

AssetViewExposure.propTypes = {
  modelMetadata: PropTypes.object.isRequired,
  context: PropTypes.object.isRequired,
  instrumentsWithMetadata: PropTypes.object.isRequired,
  highlightedRowIds: PropTypes.arrayOf(PropTypes.string),
  disableActiveTrading: PropTypes.bool.isRequired,
  instrumentColumns: PropTypes.arrayOf(PropTypes.string).isRequired,
  classifications: CLASSIFICATIONS_SHAPE.isRequired,
}
